/**
 * Shared Clerk `appearance` config for the embedded sign-in / sign-up widgets.
 *
 * Colors reference the `.technical-app-shell` CSS custom properties (see
 * styles/main.css) rather than hardcoded hex, so the widget stays in sync with
 * the rest of the authenticated product's "Recruiting Control Room" tokens.
 *
 * Hover states use opacity rather than a literal `amber-*` utility for the same
 * reason: the shell carries an Apex 25 skin for visitors arriving from
 * tryapex25.com, and a hardcoded amber hover would break out of that palette.
 */
export const clerkAppearance = {
  layout: {
    socialButtonsPlacement: "top",
    socialButtonsVariant: "iconButton",
    logoPlacement: "none",
    showOptionalFields: true,
  },
  variables: {
    colorPrimary: "var(--technical-primary)",
    colorTextOnPrimaryBackground: "var(--technical-on-primary)",
    colorText: "var(--technical-ink)",
    colorTextSecondary: "var(--technical-muted)",
    colorBackground: "#ffffff",
    colorInputBackground: "#ffffff",
    colorInputText: "var(--technical-ink)",
    colorDanger: "#e11d48",
    colorSuccess: "#059669",
    colorWarning: "var(--technical-amber)",
    colorNeutral: "var(--technical-ink)",
    borderRadius: "2px",
    fontFamily: '"General Sans", Inter, ui-sans-serif, system-ui, sans-serif',
    fontFamilyButtons: '"General Sans", Inter, ui-sans-serif, system-ui, sans-serif',
    fontSize: "0.875rem",
    fontWeight: { normal: 400, medium: 500, bold: 600 },
    spacingUnit: "1rem",
  },
  elements: {
    rootBox: "w-full",
    // Clerk's cardBox clips overflow by default (to round its own default card
    // frame). Its content-box sits ~1px inset from `card` at some viewport
    // widths, which was silently clipping the left edge of borders/rings on
    // the first field in each row. There's no framed shape left to clip to
    // once border/shadow/background are stripped, so open it back up.
    cardBox: "w-full !overflow-visible shadow-none border-none bg-transparent",
    card: "w-full max-w-none shadow-none border-none bg-transparent p-0 gap-5",
    header: "gap-1.5",
    headerTitle:
      'font-["Space_Grotesk",Inter,ui-sans-serif,system-ui,sans-serif] text-2xl font-bold text-[var(--technical-ink)]',
    headerSubtitle: "text-sm text-[var(--technical-muted)]",
    socialButtons: "gap-3",
    // Clerk renders its own "border" as a low-opacity box-shadow ring, and its
    // injected stylesheet loads after ours, so plain `border-*` utilities lose
    // the cascade and never take effect. `!` forces `!important` so ours wins.
    socialButtonsIconButton:
      "!border !border-[var(--technical-line)] hover:!bg-[var(--technical-paper)] hover:!border-slate-400 transition-colors",
    dividerRow: "gap-3",
    dividerLine: "bg-[var(--technical-line)]",
    dividerText: "text-xs text-[var(--technical-muted)]",
    formFieldLabelRow: "mb-1.5",
    formFieldLabel: "text-sm font-medium text-[var(--technical-ink)]",
    formFieldInput:
      "!border !border-[var(--technical-line)] focus:!border-slate-400 focus:!ring-2 focus:!ring-[var(--technical-amber)]/70",
    formFieldAction: "text-[var(--technical-amber)] hover:opacity-80",
    formButtonPrimary:
      "bg-[var(--technical-primary)] hover:opacity-90 text-[var(--technical-on-primary)] font-semibold normal-case shadow-none",
    footer: "bg-transparent",
    footerAction: "gap-1",
    footerActionText: "text-sm text-[var(--technical-muted)]",
    footerActionLink: "text-sm font-medium text-[var(--technical-amber)] hover:opacity-80",
    identityPreviewText: "text-[var(--technical-ink)]",
    identityPreviewEditButtonIcon: "text-[var(--technical-amber)]",
    otpCodeFieldInput: "!border !border-[var(--technical-line)] text-[var(--technical-ink)]",
    formResendCodeLink: "text-[var(--technical-amber)] hover:opacity-80",
    alternativeMethodsBlockButton: "!border !border-[var(--technical-line)] text-[var(--technical-ink)]",
  },
};
